import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Pagination from '../../components/common/Pagination';
import StyledSelectDropdown from '../../components/common/StyledSelectDropdown';
import { MatchRatingTrendChart } from '../../components/MatchRatingTrendChart';
import {
  isAbandonedMatchFeedback,
  isAbandonedSupervisorFeedback,
  isAbandonedSupervisorRatingEntry,
} from '../../utils/matchFeedbackAbandoned';
import { getCalendarDateForMatchDay } from '../../utils/matchFeedbackSchedule';
import { isDaysMatchType } from '../../utils/matchTypeUi';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import '../../styles/Modals.css';
import { FiX } from 'react-icons/fi';

const datePresets = [
  { label: 'Overall', value: 'all' },
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'Last Week', value: 'lastWeek' },
  { label: 'This Month', value: 'month' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Year', value: 'year' },
  { label: 'Custom Range', value: 'custom' },
];

const ALL_TEAMS_VALUE = '__ALL_TEAMS__';
const FEEDBACK_DIFF_OPERATORS = [
  { label: 'All Records', value: 'all' },
  { label: '=', value: 'eq', title: 'Equal to' },
  { label: '>', value: 'gt', title: 'Greater than' },
  { label: '<', value: 'lt', title: 'Less than' },
];

const round1 = (n) => (Number.isFinite(n) ? Math.round(n * 10) / 10 : null);

/** Client TL/SV diff filter — matches TL/SV column (backend list filter can disagree; see debug logs). */
const getTlSvRoundedAbsDiffForRow = (row) => {
  if (!row || row.isAbandoned || row.supervisorFeedbackAbandoned) return null;
  const tl = row.averageTotal;
  const sv = row.supervisorAverage;
  if (!Number.isFinite(tl) || !Number.isFinite(sv)) return null;
  return round1(Math.abs(tl - sv));
};

const parseTlSvDiffThreshold = (filterText) => {
  const v = Number(filterText);
  if (!Number.isFinite(v)) return null;
  return Math.max(0, Math.min(5, v));
};

const rowMatchesTlSvDiffFilter = (row, operator, filterText) => {
  if (operator === 'all') return true;
  const threshold = parseTlSvDiffThreshold(filterText);
  if (threshold === null) return true;
  const diff = getTlSvRoundedAbsDiffForRow(row);
  if (diff === null) return false;
  if (operator === 'eq') return diff === threshold;
  if (operator === 'gt') return diff > threshold;
  if (operator === 'lt') return diff < threshold;
  return true;
};

const formatDate = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const normalizeText = (value) => String(value || '').trim();

const getFeedbackDayValue = (fb) => {
  const rawDay = fb?.feedbackDay ?? fb?.dayNumber ?? fb?.matchDay ?? null;
  const day = Number(rawDay);
  return Number.isFinite(day) && day >= 1 ? day : null;
};

const getFeedbackDateForDisplay = (matchStartDate, feedbackDayNumber) => {
  if (!feedbackDayNumber) return matchStartDate;
  return getCalendarDateForMatchDay(matchStartDate, feedbackDayNumber) || matchStartDate;
};

const isDaysFeedbackRow = (fb, match) => {
  const possibleMatchType =
    match?.matchType ??
    match?.type ??
    fb?.matchType ??
    fb?.type ??
    fb?.matchId?.matchType ??
    fb?.matchId?.type;
  return isDaysMatchType(possibleMatchType);
};

/** Picks the list array from common GET /match-feedback response shapes (incl. nested `data`). */
const pickMatchFeedbackRowsArray = (obj, depth = 0) => {
  if (!obj || typeof obj !== 'object' || depth > 4) return null;
  if (Array.isArray(obj)) return obj;
  const keys = ['feedbacks', 'rows', 'results', 'docs', 'items', 'records', 'list', 'data'];
  for (const k of keys) {
    const v = obj[k];
    if (Array.isArray(v)) return v;
  }
  for (const k of keys) {
    const v = obj[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = pickMatchFeedbackRowsArray(v, depth + 1);
      if (inner) return inner;
    }
  }
  return null;
};

const getRowsFromMatchFeedbackResponse = (response) => {
  const payload = response?.success ? response?.data : response?.data || response;
  return (
    pickMatchFeedbackRowsArray(payload) ||
    pickMatchFeedbackRowsArray(response) ||
    []
  );
};

const readPaginationMetaFromObject = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const totalRaw =
    obj.total ??
    obj.totalCount ??
    obj.count ??
    obj.totalDocs ??
    obj.meta?.total ??
    obj.meta?.totalCount ??
    obj.pagination?.total ??
    obj.pagination?.totalCount;
  const total = Number(totalRaw);
  if (!Number.isFinite(total)) return null;
  const pageRaw =
    obj.page ?? obj.meta?.page ?? obj.pagination?.page ?? obj.pagination?.currentPage;
  const totalPagesRaw = obj.totalPages ?? obj.meta?.totalPages ?? obj.pagination?.totalPages;

  return {
    total,
    page: Number.isFinite(Number(pageRaw)) ? Number(pageRaw) : null,
    totalPages: Number.isFinite(Number(totalPagesRaw)) ? Number(totalPagesRaw) : null,
  };
};

const getMatchFeedbackPaginationMeta = (response, fallbackRowCount = 0) => {
  const payload = response?.success ? response?.data : response?.data || response;
  const fromRoot = readPaginationMetaFromObject(response);
  const fromPayload = readPaginationMetaFromObject(payload);
  const fromNested = readPaginationMetaFromObject(payload?.data);
  const meta = fromPayload || fromNested || fromRoot;
  if (meta) return meta;
  return { total: fallbackRowCount, page: null, totalPages: null };
};

/** Local calendar bounds aligned with GET /match-feedback `buildSubmittedAtDateFilter` presets (Mon–Sun week). */
const startOfDayLocal = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
const endOfDayLocal = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const toYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Team report: filter by Match.startDate (API matchDateFrom/matchDateTo), not feedback submittedAt.
 */
const getMatchStartDateQuery = (range, customFrom, customTo) => {
  if (range === 'all') {
    return {};
  }
  if (range === 'custom') {
    if (!customFrom || !customTo) return {};
    return { matchDateFrom: customFrom, matchDateTo: customTo };
  }

  const now = new Date();
  const currentWeekday = now.getDay();
  const daysSinceMonday = currentWeekday === 0 ? 6 : currentWeekday - 1;
  const thisWeekStart = startOfDayLocal(
    new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday),
  );
  const thisWeekEnd = endOfDayLocal(
    new Date(
      thisWeekStart.getFullYear(),
      thisWeekStart.getMonth(),
      thisWeekStart.getDate() + 6,
    ),
  );
  const lastWeekStart = startOfDayLocal(
    new Date(thisWeekStart.getFullYear(), thisWeekStart.getMonth(), thisWeekStart.getDate() - 7),
  );
  const lastWeekEnd = endOfDayLocal(
    new Date(thisWeekStart.getFullYear(), thisWeekStart.getMonth(), thisWeekStart.getDate() - 1),
  );
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  const thisYearStart = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
  const thisYearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);

  let from;
  let to;
  switch (range) {
    case 'today':
      from = startOfDayLocal(now);
      to = endOfDayLocal(now);
      break;
    case 'week':
      from = thisWeekStart;
      to = thisWeekEnd;
      break;
    case 'lastWeek':
      from = lastWeekStart;
      to = lastWeekEnd;
      break;
    case 'month':
      from = thisMonthStart;
      to = thisMonthEnd;
      break;
    case 'lastMonth':
      from = lastMonthStart;
      to = lastMonthEnd;
      break;
    case 'year':
      from = thisYearStart;
      to = thisYearEnd;
      break;
    default:
      return {};
  }

  return { matchDateFrom: toYmd(from), matchDateTo: toYmd(to) };
};

const mapMatchFeedbackToRow = (fb, index) => {
  const match = fb?.matchId || {};
  const rowTeam = fb?.eventTeamId || {};
  const evaluator = fb?.evaluator || {};
  const feedbackEntries = Array.isArray(fb?.feedback) ? fb.feedback : [];
  const parameterSums = {};
  const parameterCounts = {};

  feedbackEntries.forEach((entry) => {
    const scores = Array.isArray(entry?.scores) ? entry.scores : [];
    scores.forEach((item) => {
      const key = String(item?.parameterName || '').trim();
      const score = Number(item?.score);
      if (!key || !Number.isFinite(score)) return;
      parameterSums[key] = (parameterSums[key] || 0) + score;
      parameterCounts[key] = (parameterCounts[key] || 0) + 1;
    });
  });

  const parameterRatings = {};
  Object.keys(parameterSums).forEach((key) => {
    parameterRatings[key] = parameterCounts[key]
      ? round1(parameterSums[key] / parameterCounts[key])
      : null;
  });

  const supervisorNested = fb?.supervisorFeedback;
  const supervisorRecordForAbandon =
    supervisorNested && typeof supervisorNested === 'object'
      ? supervisorNested
      : Array.isArray(fb?.ratings) && fb.ratings.length
      ? {
          ratings: fb.ratings,
          abandoned: fb.supervisorAbandoned,
          isAbandoned: fb.supervisorIsAbandoned,
          matchStatus: fb.supervisorMatchStatus,
        }
      : null;
  const supervisorFeedbackAbandoned = supervisorRecordForAbandon
    ? isAbandonedSupervisorFeedback(supervisorRecordForAbandon)
    : false;

  const supervisorAvgRaw =
    fb?.supervisorAverageRating ??
    fb?.supervisorAvgRating ??
    fb?.supervisorAverage ??
    fb?.supervisorRating ??
    fb?.supervisorFeedback?.averageRating ??
    fb?.supervisorFeedback?.averageTotal;

  const tlFeedbackAbandoned = isAbandonedMatchFeedback(fb);
  const feedbackDayNumber = getFeedbackDayValue(fb);
  const isDaysRow = isDaysFeedbackRow(fb, match);

  return {
    id: String(fb?._id || match?._id || index),
    matchName: match?.matchName || '-',
    teamName: rowTeam?.teamName || rowTeam?.eventName || '-',
    venue: match?.matchLocation || '-',
    startDate: match?.startDate,
    isDaysMatch: isDaysRow,
    feedbackDayNumber,
    feedbackDate: getFeedbackDateForDisplay(match?.startDate, feedbackDayNumber),
    showDayBadge: isDaysRow && Number.isFinite(Number(feedbackDayNumber)),
    evaluator: evaluator?.name || evaluator?.fullName || '-',
    isAbandoned: tlFeedbackAbandoned,
    averageTotal: tlFeedbackAbandoned
      ? null
      : Number.isFinite(Number(fb?.averageTotal))
      ? Number(fb.averageTotal)
      : null,
    supervisorFeedbackAbandoned,
    supervisorAverage: supervisorFeedbackAbandoned
      ? null
      : Number.isFinite(Number(supervisorAvgRaw))
      ? Number(supervisorAvgRaw)
      : null,
    overallFeedback: String(fb?.overallFeedback || '').trim() || '-',
    supervisorOverallFeedback:
      normalizeText(
        fb?.supervisorOverallFeedback ??
          fb?.supervisorFeedback?.overallFeedback ??
          fb?.supervisorFeedback?.remarks ??
          fb?.supervisorRemarks
      ) || '-',
    supervisorEvaluator:
      normalizeText(
        fb?.supervisorEvaluator?.name ??
          fb?.supervisorEvaluator?.fullName ??
          fb?.supervisorFeedback?.evaluator?.name ??
          fb?.supervisorFeedback?.evaluator?.fullName ??
          fb?.supervisorFeedback?.evaluatorName ??
          fb?.supervisorFeedback?.reviewedBy?.name ??
          fb?.supervisorFeedback?.reviewedBy?.fullName
      ) || '-',
    feedbackEntries,
    supervisorRatings: Array.isArray(fb?.supervisorFeedback?.ratings)
      ? fb.supervisorFeedback.ratings
      : Array.isArray(fb?.ratings)
      ? fb.ratings
      : [],
    parameterRatings,
    rawFeedback: fb,
  };
};

/** Keys accepted by GET /api/match-feedback sortBy (see matchFeedbackController buildMatchFeedbackListSort). */
const SORTABLE_TEAM_FEEDBACK_COLUMNS = [
  'matchName',
  'teamName',
  'team',
  'venue',
  'startDate',
  'tlSvDiff',
  'discipline',
  'performance',
  'teamSupport',
  'communication',
  'techAccuracy',
];
const TEAM_FEEDBACK_SORT_LEFT_ALIGNED_KEYS = ['matchName', 'venue', 'teamName', 'team'];

/** Maps table header label (parameterName from data) to API sortBy value. Unknown/custom params → no server sort. */
const parameterColumnToSortKey = (displayName) => {
  const raw = String(displayName || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!raw) return null;
  const compact = raw.replace(/[^a-z0-9]+/g, '');
  if (compact === 'discipline') return 'discipline';
  if (compact === 'performance') return 'performance';
  if (compact === 'communication') return 'communication';
  if (compact === 'teamsupport' || compact === 'teamsuppport') return 'teamSupport';
  if (compact === 'techaccuracy' || compact === 'technicalaccuracy') return 'techAccuracy';
  if (raw === 'team support' || raw === 'team suppport') return 'teamSupport';
  if (
    raw === 'tech accuracy'
    || raw === 'technical accuracy'
    || raw === 'techaccuracy'
  ) {
    return 'techAccuracy';
  }
  return null;
};

const ANALYTICS_FETCH_LIMIT = 2500;

const TeamFeedbackReportPage = () => {
  const [teamId, setTeamId] = useState(ALL_TEAMS_VALUE);
  const [dateRange, setDateRange] = useState('month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [ratingDifferenceOperator, setRatingDifferenceOperator] = useState('all');
  const [ratingDifferenceFilter, setRatingDifferenceFilter] = useState('0.0');
  const [loading, setLoading] = useState(false);
  const [tableRefreshing, setTableRefreshing] = useState(false);
  const tableFeedbacksRef = useRef([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  const [sortConfig, setSortConfig] = useState({ key: 'startDate', direction: 'desc' });
  const [tableServerTotalCount, setTableServerTotalCount] = useState(0);

  const [teams, setTeams] = useState([]);
  const [tableRawFeedbacks, setTableRawFeedbacks] = useState([]);
  const [analyticsFeedbacks, setAnalyticsFeedbacks] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeDetailRow, setActiveDetailRow] = useState(null);
  /** Graph view: both series, TL only, or SV (supervisor) only */
  const [graphSeriesMode, setGraphSeriesMode] = useState('both');

  useEffect(() => {
    tableFeedbacksRef.current = tableRawFeedbacks;
  }, [tableRawFeedbacks]);

  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    let isMounted = true;

    const loadTeams = async () => {
      try {
        const pageSize = 100;
        const firstResponse = await api.getEventTeams({ page: 1, limit: pageSize });
        let rows = Array.isArray(firstResponse?.data)
          ? firstResponse.data
          : Array.isArray(firstResponse)
          ? firstResponse
          : [];
        const totalPages = Math.max(1, Number(firstResponse?.totalPages) || 1);

        for (let page = 2; page <= totalPages; page += 1) {
          const nextResponse = await api.getEventTeams({ page, limit: pageSize });
          const nextRows = Array.isArray(nextResponse?.data)
            ? nextResponse.data
            : Array.isArray(nextResponse)
            ? nextResponse
            : [];
          if (nextRows.length) rows = rows.concat(nextRows);
        }

        rows.sort((a, b) =>
          String(a?.teamName || '').localeCompare(String(b?.teamName || ''), undefined, {
            sensitivity: 'base',
          })
        );
        if (!isMounted) return;
        setTeams(rows);
      } catch (error) {
        if (!isMounted) return;
        setTeams([]);
        toast.error(error?.message || 'Failed to load teams.');
      }
    };

    loadTeams();
    return () => {
      isMounted = false;
    };
  }, []);

  const useServerTablePagination = ratingDifferenceOperator === 'all';
  const tableFetchPage = useServerTablePagination ? Math.max(1, currentPage) : 0;
  const tableFetchLimit = useServerTablePagination ? Math.max(1, itemsPerPage) : 0;

  useEffect(() => {
    let isMounted = true;

    const loadTeamFeedbacks = async () => {
      if (!teamId) {
        if (!isMounted) return;
        setTableRawFeedbacks([]);
        setTableServerTotalCount(0);
        setErrorMessage('');
        return;
      }

      if (dateRange === 'custom' && (!fromDate || !toDate)) {
        if (!isMounted) return;
        setTableRawFeedbacks([]);
        setTableServerTotalCount(0);
        setErrorMessage('Select both from and to date for custom range.');
        return;
      }

      try {
        const reuseVisibleTable = tableFeedbacksRef.current.length > 0;
        if (reuseVisibleTable) {
          setTableRefreshing(true);
        } else {
          setLoading(true);
        }
        setErrorMessage('');
        const sortByParam =
          sortConfig.key === 'team' ? 'teamName' : sortConfig.key;
        const requestFilters = {
          ...getMatchStartDateQuery(dateRange, fromDate, toDate),
          ...(teamId !== ALL_TEAMS_VALUE ? { eventTeamId: teamId } : {}),
          page: useServerTablePagination ? tableFetchPage : 1,
          limit: useServerTablePagination ? tableFetchLimit : ANALYTICS_FETCH_LIMIT,
          sortBy: sortByParam,
          sortOrder: sortConfig.direction,
        };
        const q = debouncedSearchText;
        if (q) {
          requestFilters.venue = q;
          requestFilters.matchName = q;
        }

        const response = await api.getMatchFeedbacks(requestFilters);
        const feedbackRows = getRowsFromMatchFeedbackResponse(response);
        const meta = getMatchFeedbackPaginationMeta(response, feedbackRows.length);

        if (!isMounted) return;
        setTableRawFeedbacks(feedbackRows);
        setTableServerTotalCount(useServerTablePagination ? meta.total : 0);
      } catch (error) {
        if (!isMounted) return;
        setTableRawFeedbacks([]);
        setTableServerTotalCount(0);
        setErrorMessage(error?.message || 'Failed to load team feedback report');
      } finally {
        if (isMounted) {
          setLoading(false);
          setTableRefreshing(false);
        }
      }
    };

    loadTeamFeedbacks();
    return () => {
      isMounted = false;
    };
  }, [
    teamId,
    dateRange,
    fromDate,
    toDate,
    debouncedSearchText,
    useServerTablePagination,
    tableFetchPage,
    tableFetchLimit,
    sortConfig.key,
    sortConfig.direction,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadAnalyticsFeedbacks = async () => {
      if (!teamId) {
        if (!isMounted) return;
        setAnalyticsFeedbacks([]);
        return;
      }

      if (dateRange === 'custom' && (!fromDate || !toDate)) {
        if (!isMounted) return;
        setAnalyticsFeedbacks([]);
        return;
      }

      try {
        setAnalyticsLoading(true);
        const requestFilters = {
          ...getMatchStartDateQuery(dateRange, fromDate, toDate),
          ...(teamId !== ALL_TEAMS_VALUE ? { eventTeamId: teamId } : {}),
          page: 1,
          limit: ANALYTICS_FETCH_LIMIT,
          sortBy: 'startDate',
          sortOrder: 'asc',
        };
        const q = debouncedSearchText;
        if (q) {
          requestFilters.venue = q;
          requestFilters.matchName = q;
        }

        const response = await api.getMatchFeedbacks(requestFilters);
        const rows = getRowsFromMatchFeedbackResponse(response);
        if (!isMounted) return;
        setAnalyticsFeedbacks(rows);
      } catch (error) {
        if (!isMounted) return;
        setAnalyticsFeedbacks([]);
        toast.error(error?.message || 'Failed to load team feedback analytics.');
      } finally {
        if (isMounted) setAnalyticsLoading(false);
      }
    };

    loadAnalyticsFeedbacks();
    return () => {
      isMounted = false;
    };
  }, [teamId, dateRange, fromDate, toDate, debouncedSearchText]);

  const selectedTeam = useMemo(
    () =>
      teamId === ALL_TEAMS_VALUE
        ? { teamName: 'All Teams' }
        : teams.find((item) => String(item?._id || item?.id || '') === String(teamId)),
    [teams, teamId]
  );
  const teamOptions = useMemo(
    () =>
      teams.map((item) => ({
        value: String(item?._id || item?.id || ''),
        label: item?.teamName || item?.eventName || String(item?._id || item?.id || ''),
      })),
    [teams]
  );

  const tableFilteredRawFeedbacks = useMemo(() => {
    if (useServerTablePagination) return tableRawFeedbacks;
    return tableRawFeedbacks.filter((fb, index) =>
      rowMatchesTlSvDiffFilter(
        mapMatchFeedbackToRow(fb, index),
        ratingDifferenceOperator,
        ratingDifferenceFilter
      )
    );
  }, [
    useServerTablePagination,
    tableRawFeedbacks,
    ratingDifferenceOperator,
    ratingDifferenceFilter,
  ]);

  const tablePagedRawFeedbacks = useMemo(() => {
    if (useServerTablePagination) return tableRawFeedbacks;
    const page = Math.max(1, currentPage);
    const start = (page - 1) * Math.max(1, itemsPerPage);
    return tableFilteredRawFeedbacks.slice(start, start + Math.max(1, itemsPerPage));
  }, [
    useServerTablePagination,
    tableRawFeedbacks,
    tableFilteredRawFeedbacks,
    currentPage,
    itemsPerPage,
  ]);

  const matchRows = useMemo(
    () => tablePagedRawFeedbacks.map((fb, index) => mapMatchFeedbackToRow(fb, index)),
    [tablePagedRawFeedbacks]
  );

  const analyticsMatchRows = useMemo(
    () => analyticsFeedbacks.map((fb, index) => mapMatchFeedbackToRow(fb, index)),
    [analyticsFeedbacks]
  );

  const analyticsMatchRowsFiltered = useMemo(() => {
    if (ratingDifferenceOperator === 'all') return analyticsMatchRows;
    return analyticsMatchRows.filter((row) =>
      rowMatchesTlSvDiffFilter(row, ratingDifferenceOperator, ratingDifferenceFilter)
    );
  }, [analyticsMatchRows, ratingDifferenceOperator, ratingDifferenceFilter]);

  const rowsForAverages = useMemo(
    () => analyticsMatchRowsFiltered.filter((row) => !row.isAbandoned),
    [analyticsMatchRowsFiltered]
  );

  const effectiveTableTotalCount = useServerTablePagination
    ? tableServerTotalCount
    : tableFilteredRawFeedbacks.length;

  const totalPages = Math.max(
    1,
    Math.ceil(Math.max(0, effectiveTableTotalCount) / Math.max(1, itemsPerPage))
  );
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const parameterSummary = useMemo(() => {
    const sums = {};
    const counts = {};

    rowsForAverages.forEach((row) => {
      row.feedbackEntries.forEach((entry) => {
        const scores = Array.isArray(entry?.scores) ? entry.scores : [];
        scores.forEach((item) => {
          const key = String(item?.parameterName || '').trim();
          const score = Number(item?.score);
          if (!key || !Number.isFinite(score)) return;
          sums[key] = (sums[key] || 0) + score;
          counts[key] = (counts[key] || 0) + 1;
        });
      });
    });

    const keys = Object.keys(sums);
    const averagesByParam = {};
    keys.forEach((key) => {
      averagesByParam[key] = counts[key] ? round1(sums[key] / counts[key]) : null;
    });

    return { keys, averagesByParam };
  }, [rowsForAverages]);

  const overallRating = useMemo(() => {
    const values = rowsForAverages
      .map((row) => row.averageTotal)
      .filter((value) => Number.isFinite(value));
    if (!values.length) return 0;
    return values.reduce((acc, value) => acc + value, 0) / values.length;
  }, [rowsForAverages]);

  const ratingTitle = useMemo(() => {
    const selectedPreset = datePresets.find((preset) => preset.value === dateRange);
    const selectedLabel = selectedPreset?.label || 'Overall';
    return selectedLabel.toLowerCase() === 'overall'
      ? 'Overall Team Rating'
      : `${selectedLabel} Team Rating`;
  }, [dateRange]);

  const matchGraphRows = useMemo(() => {
    const ordered = [...rowsForAverages]
      .filter(
        (row) => Number.isFinite(row.averageTotal) || Number.isFinite(row.supervisorAverage)
      )
      .sort((left, right) => {
        const leftTime = new Date(left.startDate).getTime();
        const rightTime = new Date(right.startDate).getTime();
        if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) return leftTime - rightTime;
        if (Number.isFinite(leftTime)) return -1;
        if (Number.isFinite(rightTime)) return 1;
        return String(left.matchName || '').localeCompare(String(right.matchName || ''));
      });

    return ordered.map((row, index) => ({
      id: row.id,
      index: index + 1,
      label: `M${index + 1}`,
      matchName: row.matchName || '-',
      value: Number.isFinite(row.averageTotal) ? Number(row.averageTotal) : null,
      svValue: Number.isFinite(row.supervisorAverage) ? Number(row.supervisorAverage) : null,
    }));
  }, [rowsForAverages]);

  const detailSummary = useMemo(() => {
    if (!activeDetailRow) return null;

    const feedbackEntries = Array.isArray(activeDetailRow.feedbackEntries)
      ? activeDetailRow.feedbackEntries
      : [];
    const supervisorRatings = Array.isArray(activeDetailRow.supervisorRatings)
      ? activeDetailRow.supervisorRatings
      : [];
    const isTlAbandoned = Boolean(activeDetailRow?.isAbandoned);

    const parameterOrder = [];
    const seenParams = new Set();
    const roleMap = new Map();

    feedbackEntries.forEach((entry) => {
      const role = normalizeText(entry?.role) || 'Unknown';
      if (!roleMap.has(role)) {
        roleMap.set(role, {
          role,
          members: new Set(),
          remarks: [],
          parameterSums: {},
          parameterCounts: {},
          totalSum: 0,
          totalCount: 0,
        });
      }
      const roleBucket = roleMap.get(role);
      const memberName =
        normalizeText(entry?.employeeId?.name) ||
        normalizeText(entry?.employeeId?.fullName) ||
        normalizeText(entry?.employeeName);
      if (memberName) roleBucket.members.add(memberName);
      const entryRemark = normalizeText(entry?.remarks);
      if (entryRemark) roleBucket.remarks.push(entryRemark);

      const scores = Array.isArray(entry?.scores) ? entry.scores : [];
      scores.forEach((item) => {
        const key = normalizeText(item?.parameterName);
        const score = Number(item?.score);
        if (!seenParams.has(key)) {
          seenParams.add(key);
          parameterOrder.push(key);
        }
        if (!key || !Number.isFinite(score)) return;
        roleBucket.parameterSums[key] = (roleBucket.parameterSums[key] || 0) + score;
        roleBucket.parameterCounts[key] = (roleBucket.parameterCounts[key] || 0) + 1;
      });

      const total = Number(entry?.total);
      if (Number.isFinite(total)) {
        roleBucket.totalSum += total;
        roleBucket.totalCount += 1;
      }
    });

    const roleRows = Array.from(roleMap.values()).map((bucket) => {
      const parameterAverages = {};
      parameterOrder.forEach((key) => {
        const count = bucket.parameterCounts[key] || 0;
        parameterAverages[key] = count ? round1((bucket.parameterSums[key] || 0) / count) : null;
      });

      return {
        role: bucket.role,
        members: Array.from(bucket.members),
        remarks: [...new Set(bucket.remarks)].join('; ') || '-',
        parameterAverages,
        roleAverage:
          isTlAbandoned || !bucket.totalCount
            ? null
            : round1(bucket.totalSum / bucket.totalCount),
      };
    });

    const tlRemarks = [
      ...new Set(
        feedbackEntries
          .map((entry) => normalizeText(entry?.remarks))
          .filter(Boolean)
      ),
    ];

    const supervisorRemarks = [
      ...new Set(
        supervisorRatings
          .map((entry) => normalizeText(entry?.remarks))
          .filter(Boolean)
      ),
    ];

    return {
      parameterOrder,
      roleRows,
      tlRemarks,
      supervisorRemarks,
    };
  }, [activeDetailRow]);

  const handleViewDetails = (row) => {
    if (!row) return;
    setActiveDetailRow(row);
  };

  const changeDifferenceValue = (delta) => {
    const current = Number(ratingDifferenceFilter);
    const safeCurrent = Number.isFinite(current) ? current : 0;
    const next = Math.max(0, Math.min(5, round1(safeCurrent + delta) ?? 0));
    setRatingDifferenceFilter(next.toFixed(1));
    setCurrentPage(1);
  };

  const clampAndFormatDifferenceInput = (raw) => {
    const trimmed = String(raw ?? '').trim();
    if (trimmed === '' || trimmed === '.') return '0.0';
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return '0.0';
    const clamped = Math.max(0, Math.min(5, round1(n) ?? 0));
    return clamped.toFixed(1);
  };

  const handleRatingDifferenceInputChange = (event) => {
    if (ratingDifferenceOperator === 'all') return;
    const next = event.target.value;
    if (next !== '' && !/^\d*\.?\d*$/.test(next)) return;
    setRatingDifferenceFilter(next);
    setCurrentPage(1);
  };

  const handleRatingDifferenceInputBlur = () => {
    if (ratingDifferenceOperator === 'all') return;
    setRatingDifferenceFilter((prev) => clampAndFormatDifferenceInput(prev));
  };

  const closeDetailsModal = () => setActiveDetailRow(null);

  const handleSort = (key) => {
    if (!SORTABLE_TEAM_FEEDBACK_COLUMNS.includes(key)) return;
    const normalizedKey = key === 'team' ? 'teamName' : key;
    setSortConfig((prev) => {
      const prevNorm = prev.key === 'team' ? 'teamName' : prev.key;
      return {
        key: normalizedKey,
        direction: prevNorm === normalizedKey && prev.direction === 'asc' ? 'desc' : 'asc',
      };
    });
    setCurrentPage(1);
  };

  const getSortIcon = (key) => {
    const activeKey = sortConfig.key === 'team' ? 'teamName' : sortConfig.key;
    const compareKey = key === 'team' ? 'teamName' : key;
    if (activeKey !== compareKey) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const renderSortableHeader = (key, label, title, extraThStyle = {}) => {
    const activeKey = sortConfig.key === 'team' ? 'teamName' : sortConfig.key;
    const compareKey = key === 'team' ? 'teamName' : key;
    const isActive = activeKey === compareKey;
    const textAlign = TEAM_FEEDBACK_SORT_LEFT_ALIGNED_KEYS.includes(key) ? 'left' : 'center';
    return (
      <th
        key={key}
        scope="col"
        style={{
          textAlign,
          padding: '9px 10px',
          color: isActive ? '#93c5fd' : '#f8fafc',
          fontSize: '12px',
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          fontWeight: 600,
          ...extraThStyle,
        }}
        onClick={() => handleSort(key)}
        title={title}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            justifyContent: textAlign === 'center' ? 'center' : 'flex-start',
            width: '100%',
          }}
        >
          <span>{label}</span>
          <span style={{ color: isActive ? '#93c5fd' : '#cbd5e1', fontSize: '10px' }}>{getSortIcon(key)}</span>
        </span>
      </th>
    );
  };

  return (
    <div style={{ padding: '16px' }}>
      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '14px',
          padding: '14px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '14px',
            alignItems: 'stretch',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0, height: '100%' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '12px',
                alignItems: 'start',
                width: '100%',
              }}
            >
              <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                <label style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>
                  TEAM
                </label>
                <StyledSelectDropdown
                  ariaLabel="Filter by team"
                  placeholder="All Teams"
                  emptyOptionLabel="Select team"
                  value={teamId}
                  onChange={(value) => {
                    setTeamId(value || ALL_TEAMS_VALUE);
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: ALL_TEAMS_VALUE, label: 'All Teams' },
                    ...teamOptions,
                  ]}
                />
              </div>

              <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>SEARCH MATCH / VENUE</div>
                <input
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Search match or venue"
                  style={{
                    width: '100%',
                    border: '1px solid #d0d5dd',
                    borderRadius: '7px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontWeight: 400,
                    background: '#fff',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '2px' }}>DATE RANGE</div>
                {/* <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '6px', lineHeight: 1.3 }}>
                  By match start date
                </div> */}
                <select
                  value={dateRange}
                  onChange={(event) => {
                    setDateRange(event.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    width: '100%',
                    border: '1px solid #d0d5dd',
                    borderRadius: '7px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontWeight: 400,
                    background: '#fff',
                    boxSizing: 'border-box',
                  }}
                >
                  {datePresets.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>
                  FEEDBACK DIFFERENCE (TL/SV)
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <select
                    value={ratingDifferenceOperator}
                    onChange={(event) => {
                      setRatingDifferenceOperator(event.target.value);
                      setCurrentPage(1);
                    }}
                    style={{
                      border: '1px solid #d0d5dd',
                      borderRadius: '7px',
                      padding: '8px 10px',
                      fontSize: '12px',
                      fontWeight: 400,
                      background: '#fff',
                      flex: '1 1 0',
                      minWidth: 0,
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {FEEDBACK_DIFF_OPERATORS.map((item) => (
                      <option key={item.value} value={item.value} title={item.title || item.label}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexShrink: 0,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => changeDifferenceValue(-0.1)}
                      disabled={ratingDifferenceOperator === 'all'}
                      style={{
                        border: '1px solid #d0d5dd',
                        borderRadius: '7px',
                        padding: '6px 10px',
                        fontSize: '14px',
                        lineHeight: 1,
                        background: 'white',
                        cursor: ratingDifferenceOperator === 'all' ? 'not-allowed' : 'pointer',
                        minWidth: '28px',
                      }}
                    >
                      -
                    </button>
                    <input
                      type="text"
                      inputMode="decimal"
                      readOnly={ratingDifferenceOperator === 'all'}
                      value={ratingDifferenceOperator === 'all' ? '—' : ratingDifferenceFilter}
                      onChange={handleRatingDifferenceInputChange}
                      onBlur={handleRatingDifferenceInputBlur}
                      title={
                        ratingDifferenceOperator === 'all'
                          ? 'Threshold not used when showing all records'
                          : 'TL vs supervisor rating difference threshold (0.0–5.0; type or use +/-)'
                      }
                      style={{
                        border: '1px solid #d0d5dd',
                        borderRadius: '7px',
                        padding: '6px 6px',
                        fontSize: '12px',
                        fontWeight: 400,
                        width: '52px',
                        textAlign: 'center',
                        background: ratingDifferenceOperator === 'all' ? '#f1f5f9' : '#fff',
                        boxSizing: 'border-box',
                        color: ratingDifferenceOperator === 'all' ? '#64748b' : 'inherit',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => changeDifferenceValue(0.1)}
                      disabled={ratingDifferenceOperator === 'all'}
                      style={{
                        border: '1px solid #d0d5dd',
                        borderRadius: '7px',
                        padding: '6px 10px',
                        fontSize: '14px',
                        lineHeight: 1,
                        background: 'white',
                        cursor: ratingDifferenceOperator === 'all' ? 'not-allowed' : 'pointer',
                        minWidth: '28px',
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {dateRange === 'custom' && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(event) => {
                    setFromDate(event.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    border: '1px solid #d0d5dd',
                    borderRadius: '7px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontWeight: 400,
                    background: '#fff',
                    flex: '1 1 140px',
                    minWidth: 0,
                  }}
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={(event) => {
                    setToDate(event.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    border: '1px solid #d0d5dd',
                    borderRadius: '7px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontWeight: 400,
                    background: '#fff',
                    flex: '1 1 140px',
                    minWidth: 0,
                  }}
                />
              </div>
            )}

            <div
              style={{
                borderRadius: '14px',
                padding: '14px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                minHeight: '120px',
              }}
            >
              <div style={{ fontSize: '11px', opacity: 0.85 }}>{ratingTitle}</div>
              <div style={{ marginTop: '6px', fontSize: '32px', lineHeight: 1, fontWeight: 700 }}>
                {overallRating.toFixed(1)}
              </div>
              <div style={{ marginTop: '6px', fontSize: '12px', opacity: 0.95 }}>
                <span style={{ fontSize: '14px', fontWeight: 700 }}>
                  ({effectiveTableTotalCount} Matches)
                </span>
                {analyticsLoading ? ' · refreshing chart…' : ''}
              </div>
            </div>
          </div>

          <div
            style={{
              borderRadius: '16px',
              padding: '14px',
              boxSizing: 'border-box',
              minHeight: '220px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              color: '#1e293b',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              background: '#ffffff',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 4px 14px rgba(15, 23, 42, 0.06)',
              border: '1px solid #e2e8f0',
            }}
          >
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                marginBottom: '10px',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  color: '#334155',
                  fontFamily:
                    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '4px',
                }}
              >
                <span>Team</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span
                    style={{
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      background: '#ef4444',
                      flexShrink: 0,
                    }}
                    aria-hidden
                  />
                  TL
                </span>
                <span> & </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span
                    style={{
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      background: '#22c55e',
                      flexShrink: 0,
                    }}
                    aria-hidden
                  />
                  SV
                </span>
                <span>rating trend (match-wise)</span>
              </div>
              <div
                role="group"
                aria-label="Chart series"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  alignItems: 'center',
                }}
              >
                {[
                  { id: 'both', label: 'Both' },
                  { id: 'tl', label: 'TL only' },
                  { id: 'sv', label: 'SV only' },
                ].map((btn) => {
                  const active = graphSeriesMode === btn.id;
                  let bg = '#f1f5f9';
                  let border = '1px solid #cbd5e1';
                  let color = '#475569';
                  if (active) {
                    if (btn.id === 'tl') {
                      bg = '#f43f5e';
                      border = '1px solid #fb7185';
                      color = '#ffffff';
                    } else if (btn.id === 'sv') {
                      bg = '#22c55e';
                      border = '1px solid #4ade80';
                      color = '#ffffff';
                    } else {
                      bg = '#2563eb';
                      border = '1px solid #3b82f6';
                      color = '#ffffff';
                    }
                  }
                  return (
                    <button
                      key={btn.id}
                      type="button"
                      onClick={() => setGraphSeriesMode(btn.id)}
                      aria-pressed={active}
                      style={{
                        border,
                        borderRadius: '8px',
                        padding: '6px 10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: bg,
                        color,
                        fontFamily: 'inherit',
                      }}
                    >
                      {btn.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {matchGraphRows.length ? (
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
                <MatchRatingTrendChart rows={matchGraphRows} fillSpace seriesMode={graphSeriesMode} variant="light" />
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: '#64748b', padding: '28px 6px', textAlign: 'center', flex: 1 }}>
                No TL average ratings for selected filters.
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: '14px',
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '14px',
          padding: '14px',
        }}
      >
        {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '20px' }}>Team Feedback Report</h3>
          <div style={{ color: '#64748b', fontSize: '13px' }}>{selectedTeam?.teamName || selectedTeam?.eventName || '-'}</div>
        </div> */}

        {loading && (
          <div style={{ marginTop: '8px', color: '#64748b', fontSize: '12px' }}>Loading report...</div>
        )}
        {!loading && errorMessage && (
          <div style={{ marginTop: '8px', color: '#dc2626', fontSize: '12px' }}>{errorMessage}</div>
        )}

        <div
          style={{
            marginTop: '12px',
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.max(parameterSummary.keys.length, 1)}, minmax(120px, 1fr))`,
            gap: '10px',
          }}
        >
          {parameterSummary.keys.length ? (
            parameterSummary.keys.map((key) => (
              <div key={key} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                  {key}
                </div>
                <div style={{ color: '#2563eb', marginTop: '4px', fontSize: '26px', lineHeight: 1, fontWeight: 700 }}>
                  {parameterSummary.averagesByParam[key] ?? 'NA'}
                </div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Rating</div>
              </div>
            ))
          ) : (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', color: '#64748b', fontSize: '12px' }}>
              {teamId && !loading
                ? 'No parameter scores found for this team in selected filters.'
                : 'Select a team to load feedback criteria.'}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: '16px',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            width: '100%',
            WebkitOverflowScrolling: 'touch',
            position: 'relative',
          }}
        >
          <div
            style={{
              opacity: tableRefreshing ? 0.88 : 1,
              transition: 'opacity 0.2s ease-out',
              pointerEvents: tableRefreshing ? 'none' : 'auto',
            }}
          >
          <table style={{ minWidth: '920px', width: '100%', whiteSpace: 'nowrap' }}>
            <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', whiteSpace: 'nowrap' }}>
              <tr>
                <th
                  scope="col"
                  style={{
                    textAlign: 'center',
                    padding: '9px 10px',
                    color: '#f8fafc',
                    fontSize: '12px',
                    fontWeight: 600,
                    width: '44px',
                  }}
                >
                  S.No
                </th>
                {renderSortableHeader('matchName', 'Match', 'Sort by match name')}
                {teamId === ALL_TEAMS_VALUE
                  ? renderSortableHeader(
                      'teamName',
                      'Team',
                      'Sort by team name (EventTeam)',
                    )
                  : null}
                {renderSortableHeader('venue', 'Venue', 'Sort by venue')}
                {renderSortableHeader('startDate', 'Date', 'Sort by match date', { width: '120px' })}
                {parameterSummary.keys.map((paramKey) => {
                  const sortField = parameterColumnToSortKey(paramKey);
                  if (sortField) {
                    return renderSortableHeader(
                      sortField,
                      paramKey,
                      `Sort by ${paramKey} (TL parameter average)`,
                    );
                  }
                  return (
                    <th
                      key={paramKey}
                      scope="col"
                      style={{
                        textAlign: 'center',
                        padding: '9px 10px',
                        color: '#f8fafc',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {paramKey}
                    </th>
                  );
                })}
                {renderSortableHeader('tlSvDiff', 'TL / SV', 'Sort by TL vs supervisor rating difference')}
                <th
                  scope="col"
                  style={{
                    textAlign: 'center',
                    padding: '9px 10px',
                    color: '#f8fafc',
                    fontSize: '12px',
                    fontWeight: 600,
                    width: '112px',
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && matchRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={8 + parameterSummary.keys.length + (teamId === ALL_TEAMS_VALUE ? 1 : 0)}
                    style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}
                  >
                    Loading report…
                  </td>
                </tr>
              ) : matchRows.length ? (
                matchRows.map((row, index) => (
                  <tr key={row.id}>
                    <td style={{ textAlign: 'center' }}>{(safePage - 1) * itemsPerPage + index + 1}</td>
                    <td>
                      {row.matchName}
                      {row.showDayBadge && row.feedbackDayNumber ? ` (Day ${row.feedbackDayNumber})` : ''}
                    </td>
                    {teamId === ALL_TEAMS_VALUE && <td>{row.teamName}</td>}
                    <td>{row.venue}</td>
                    <td style={{ textAlign: 'center' }}>
                      {formatDate(row.feedbackDate || row.startDate)}
                    </td>
                    {/* <td>{row.evaluator}</td> */}
                    {parameterSummary.keys.map((key) => (
                      <td
                        key={`${row.id}-${key}`}
                        style={{ textAlign: 'center' }}
                      >
                        {Number.isFinite(Number(row.parameterRatings[key]))
                          ? Number(row.parameterRatings[key]).toFixed(1)
                          : 'NA'}
                      </td>
                    ))}
                    <td style={{ textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          background: '#dbeafe',
                          color: '#1d4ed8',
                          borderRadius: '999px',
                          padding: '3px 10px',
                          fontWeight: 700,
                          fontSize: '12px',
                        }}
                      >
                        {(row.averageTotal === null ? 'NA' : row.averageTotal.toFixed(1))}/
                        {row.supervisorAverage === null ? 'NA' : row.supervisorAverage.toFixed(1)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handleViewDetails(row)}
                        style={{
                          border: '1px solid #93c5fd',
                          background: 'white',
                          color: '#2563eb',
                          borderRadius: '8px',
                          padding: '5px 10px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        View Details
                      </button>
                    </td>
                    {/* <td>{row.overallFeedback}</td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8 + parameterSummary.keys.length + (teamId === ALL_TEAMS_VALUE ? 1 : 0)} style={{ textAlign: 'center', color: '#64748b' }}>
                    No feedback entries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        {teamId && !(dateRange === 'custom' && (!fromDate || !toDate)) && !errorMessage && (
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            totalItems={effectiveTableTotalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(value) => {
              setItemsPerPage(value);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {activeDetailRow && (
        <div
          onClick={closeDetailsModal}
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '18px',
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            style={{
              width: 'min(1100px, 100%)',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'white',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 18px 36px rgba(2, 6, 23, 0.22)',
              padding: '16px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '20px' }}>Feedback Details</h3>
                {/* <div style={{ marginTop: '4px', color: '#475569', fontSize: '13px' }}>
                  {activeDetailRow.matchName} - {selectedTeam?.teamName || selectedTeam?.eventName || '-'}
                </div> */}
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={closeDetailsModal}
                style={{
                  border: '1px solid #e8ecf0',
                  background: '#f9fafb',
                  color: '#64748b',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <FiX size={17} strokeWidth={2} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                {[
                { label: 'Match', value: activeDetailRow.matchName },
                  {
                    label: 'Team',
                    value:
                      (teamId === ALL_TEAMS_VALUE
                        ? activeDetailRow.teamName
                        : selectedTeam?.teamName || selectedTeam?.eventName) || '-',
                  },
                { label: 'Venue', value: activeDetailRow.venue },
                { label: 'Date', value: formatDate(activeDetailRow.startDate) },
                // { label: 'TL Evaluator', value: activeDetailRow.evaluator || '-' },
                {
                  label: 'TL Avg Rating',
                  value: activeDetailRow.averageTotal === null ? 'NA' : activeDetailRow.averageTotal.toFixed(1),
                  accent: 'tl',
                },
                {
                  label: 'Supervisor Avg Rating',
                  value:
                    activeDetailRow.supervisorAverage === null
                      ? 'NA'
                      : activeDetailRow.supervisorAverage.toFixed(1),
                  accent: 'supervisor',
                },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    border:
                      item.accent === 'tl'
                        ? '1px solid #f8d7da'
                        : item.accent === 'supervisor'
                        ? '1px solid #d1f2dc'
                        : '1px solid #e2e8f0',
                    borderRadius: '10px',
                    padding: '10px',
                    background:
                      item.accent === 'tl'
                        ? '#fff8f8'
                        : item.accent === 'supervisor'
                        ? '#f6fff8'
                        : '#ffffff',
                  }}
                >
                  <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>{item.label}</div>
                  <div
                    style={{
                      marginTop: '3px',
                      fontSize: '13px',
                      color:
                        item.accent === 'tl'
                          ? '#334155'
                          : item.accent === 'supervisor'
                          ? '#334155'
                          : '#0f172a',
                      fontWeight: 700,
                    }}
                  >
                    {item.value || '-'}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                border: '1px solid #fecaca',
                borderRadius: '10px',
                overflow: 'hidden',
                marginBottom: '12px',
                background: '#fef2f2',
              }}
            >
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#991b1b',
                  background: '#fee2e2',
                  borderBottom: '1px solid #fecaca',
                }}
              >
                TL Feedback
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ minWidth: `${360 + Math.max(detailSummary?.parameterOrder?.length || 0, 1) * 110}px`, width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: '#fff1f2' }}>
                    <tr>
                      <th style={{ padding: '9px 10px', textAlign: 'left', fontSize: '12px' }}>Job Type</th>
                      <th style={{ padding: '9px 10px', textAlign: 'left', fontSize: '12px' }}>Members</th>
                      {(detailSummary?.parameterOrder || []).map((key) => (
                        <th key={`modal-param-${key}`} style={{ padding: '9px 10px', textAlign: 'center', fontSize: '12px' }}>
                          {key}
                        </th>
                      ))}
                      <th style={{ padding: '9px 10px', textAlign: 'center', fontSize: '12px' }}>Average</th>
                      <th style={{ padding: '9px 10px', textAlign: 'center', fontSize: '12px' }}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailSummary?.roleRows?.length ? (
                      detailSummary.roleRows.map((roleRow) => (
                        <tr key={`modal-role-${roleRow.role}`} style={{ borderTop: '1px solid #fecdd3' }}>
                          <td style={{ padding: '9px 10px', fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{roleRow.role}</td>
                          <td style={{ padding: '9px 10px', fontSize: '12px', color: '#334155' }}>
                            {roleRow.members.length ? roleRow.members.join(', ') : '-'}
                          </td>
                          {(detailSummary.parameterOrder || []).map((key) => (
                            <td key={`modal-role-${roleRow.role}-${key}`} style={{ padding: '9px 10px', textAlign: 'center', fontSize: '12px', color: '#334155' }}>
                              {roleRow.parameterAverages[key] ?? 'NA'}
                            </td>
                          ))}
                          <td style={{ padding: '9px 10px', textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#b91c1c' }}>
                            {roleRow.roleAverage ?? 'NA'}
                          </td>
                          {/* <td style={{ padding: '9px 10px', fontSize: '12px', color: '#334155' }}>{roleRow.roleAverage ?? 'NA'}</td> */}
                          <td style={{ padding: '9px 10px', textAlign: 'center', fontSize: '12px', color: '#334155' }}>{normalizeText(roleRow.remarks) || '-'}</td>
                        </tr>
                      ))
                      
                    ) : (
                      <tr>
                        <td colSpan={3 + (detailSummary?.parameterOrder?.length || 0)} style={{ padding: '12px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                          No TL feedback entries available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                
              </div>
              
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}> 
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '11px', color: '#475569', fontWeight: 700, marginBottom: '6px' }}>TL Overall Feedback</div>
                <div style={{ fontSize: '12px', color: '#0f172a', lineHeight: 1.6 }}>
                  {normalizeText(activeDetailRow.overallFeedback) || '-'}
                </div>
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '11px', color: '#475569', fontWeight: 700, marginBottom: '6px' }}>Evaluator</div>
                <div style={{ fontSize: '12px', color: '#0f172a', lineHeight: 1.6 }}>
                  {normalizeText(activeDetailRow.evaluator) || '-'}
                </div>
              </div>
            </div>

            <div
              style={{
                border: '1px solid #6ee7b7',
                borderRadius: '10px',
                overflow: 'hidden',
                marginBottom: '12px',
                marginTop: '12px',
                background: '#f0fdf4',
              }}
            >
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#065f46',
                  background: '#d1fae5',
                  borderBottom: '1px solid #6ee7b7',
                }}
              >
                Supervisor Feedback
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ minWidth: '640px', width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: '#ecfdf5' }}>
                    <tr>
                      <th style={{ padding: '9px 10px', textAlign: 'left', fontSize: '12px' }}>Job Type</th>
                      <th style={{ padding: '9px 10px', textAlign: 'left', fontSize: '12px' }}>Member</th>
                      <th style={{ padding: '9px 10px', textAlign: 'center', fontSize: '12px' }}>Rating</th>
                      <th style={{ padding: '9px 10px', textAlign: 'left', fontSize: '12px' }}>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeDetailRow.supervisorRatings.length ? (
                      activeDetailRow.supervisorRatings.map((rating, idx) => (
                        <tr key={`supervisor-rating-${idx}`} style={{ borderTop: '1px solid #a7f3d0' }}>
                          <td style={{ padding: '9px 10px', fontSize: '12px', color: '#0f172a' }}>{rating?.role || '-'}</td>
                          <td style={{ padding: '9px 10px', fontSize: '12px', color: '#334155' }}>
                            {rating?.employeeId?.name || rating?.employeeId?.fullName || '-'}
                          </td>
                          <td style={{ padding: '9px 10px', textAlign: 'center', fontSize: '12px', color: '#047857', fontWeight: 700 }}>
                            {activeDetailRow.supervisorFeedbackAbandoned ||
                            isAbandonedSupervisorRatingEntry(rating)
                              ? 'NA'
                              : Number.isFinite(Number(rating?.rating))
                              ? Number(rating.rating).toFixed(1)
                              : 'NA'}
                          </td>
                          <td style={{ padding: '9px 10px', fontSize: '12px', color: '#334155' }}>{normalizeText(rating?.remarks) || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} style={{ padding: '12px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                          No supervisor feedback details available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}> 
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '11px', color: '#475569', fontWeight: 700, marginBottom: '6px' }}>Supervisor Overall Feedback</div>
                <div style={{ fontSize: '12px', color: '#0f172a', lineHeight: 1.6 }}>
                  {normalizeText(activeDetailRow.supervisorOverallFeedback) || '-'}
                </div>
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '11px', color: '#475569', fontWeight: 700, marginBottom: '6px' }}>Supervisor Evaluator</div>
                <div style={{ fontSize: '12px', color: '#0f172a', lineHeight: 1.6 }}>
                  {normalizeText(activeDetailRow.supervisorEvaluator) || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamFeedbackReportPage;
